import {Box, createStyles, Group, Stack, Text} from '@mantine/core';
import React, {useMemo} from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {BaseCondition} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import getTestProps from 'test/lib/cypress/getTestProps';

import {
  ArchivedDisplayOptions,
  buildTransposedReplicateTableRows,
  summarizeTransposedReplicateRows,
  TransposedReplicateTable,
} from './TransposedReplicateTable';

interface TransposedReplicateTableSectionProps<TCondition extends BaseCondition> {
  conditions: Array<TCondition>;
  setConditions: (conditions: Array<TCondition>, prevIndexes: Array<number | null>) => void;
  experimentalInputs: ExperimentalInputs | null;
  setExperimentalInputs: (experimentalInputs: ExperimentalInputs) => void;
  editNumColsButtons: JSX.Element;
  locked: boolean;
  procedureType: ProcedureType;
  newConditionCoveredMethodIds?: ReadonlyArray<string>;
  isPlan?: boolean;
  archivedDisplay?: ArchivedDisplayOptions;
  dataTestAttrs?: {[key: string]: string};
}

/**
 * Conditions title, replicate summary, transposed conditions table, and paste hint.
 */
export function TransposedReplicateTableSection<TCondition extends BaseCondition>(
  props: TransposedReplicateTableSectionProps<TCondition>
): JSX.Element {
  const {classes} = useStyles();
  const isPlan = props.isPlan ?? false;
  const precomputedRows = useMemo(
    () =>
      buildTransposedReplicateTableRows(props.conditions, {
        isPlan,
        archivedDisplay: props.archivedDisplay,
      }),
    [props.conditions, isPlan, props.archivedDisplay]
  );
  const conditionCountText = summarizeTransposedReplicateRows(precomputedRows.rows, props.procedureType);
  const hasAtLeastOneUnlockedRow = !props.locked && precomputedRows.rows.some((row) => !row.locked);
  const rightHeaderControl = !props.locked ? props.editNumColsButtons : undefined;

  return (
    <Box w="100%" {...getTestProps({component: 'TransposedReplicateTableSection', element: 'root'})}>
      <Group position="apart" noWrap={true} className={classes.header}>
        <Stack spacing="xxs">
          <Text className={classes.conditionsLabel}>Conditions</Text>
          <Text className={classes.countLabel}>{conditionCountText}</Text>
        </Stack>
        {rightHeaderControl}
      </Group>
      <TransposedReplicateTable
        conditions={props.conditions}
        setConditions={props.setConditions}
        experimentalInputs={props.experimentalInputs}
        setExperimentalInputs={props.setExperimentalInputs}
        locked={props.locked}
        procedureType={props.procedureType}
        newConditionCoveredMethodIds={props.newConditionCoveredMethodIds}
        isPlan={isPlan}
        archivedDisplay={props.archivedDisplay}
        dataTestAttrs={props.dataTestAttrs}
        precomputedRows={precomputedRows}
      />
      {hasAtLeastOneUnlockedRow ? (
        <Text className={classes.pasteHelperText}>
          {`Paste from a spreadsheet to add multiple ${getProcedureRunConditionTextForProcedureType(
            props.procedureType,
            {plural: true}
          )}`}
        </Text>
      ) : null}
    </Box>
  );
}

const useStyles = createStyles((theme) => ({
  header: {
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.xs,
  },
  conditionsLabel: {
    color: theme.black,
    fontWeight: 600,
    fontSize: theme.fontSizes.md,
    lineHeight: '20px',
  },
  countLabel: {
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.xs,
    lineHeight: '16px',
  },
  pasteHelperText: {
    marginTop: theme.spacing.xs,
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.xs,
    lineHeight: '14px',
  },
}));
